'use client';

import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { ChevronUpDownIcon } from '@heroicons/react/24/solid';
import { Button, cn } from '@heroui/react';
import clsx from 'clsx';
import _ from 'lodash';
import type React from 'react';
import { createContext, forwardRef, useContext, useEffect, useState } from 'react';
import { Link } from './link.tsx';

export interface TableSort {
  column: string;
  direction: 'asc' | 'desc';
}

interface SortStateProps<T> {
  data: T[];
  // NOTE: This function should be memoized with useCallback to prevent infinite re-renders
  memoizedSortingDataAccessor?: (value: T, column: string) => any;
}

const defaultSortingDataAccessor = (value, column) => _.get(value, column);

export function useSort<T>({ data, memoizedSortingDataAccessor }: SortStateProps<T>) {
  const [sort, setSort] = useState<TableSort>(null);
  const [sortData, setSortData] = useState(data ?? []);

  const stableSortingDataAccessor = memoizedSortingDataAccessor || defaultSortingDataAccessor;

  useEffect(() => {
    const dataArray = data ?? [];
    if (sort?.column && dataArray.length) {
      const sorted = [...dataArray].sort((a, b) => {
        const curr = stableSortingDataAccessor(a, sort.column);
        const next = stableSortingDataAccessor(b, sort.column);
        let result = 0;
        if ((curr && !next) || curr > next) {
          result = 1;
        }

        if ((!curr && next) || curr < next) {
          result = -1;
        }

        return sort.direction === 'desc' ? result * -1 : result;
      });

      setSortData(sorted);
    } else {
      setSortData(dataArray);
    }
  }, [sort, data, stableSortingDataAccessor]);

  return {
    sort,
    setSort,
    sortData,
  };
}

const TableContext = createContext<{
  bleed: boolean;
  dense: boolean;
  grid: boolean;
  currentSort?: TableSort;
  setCurrentSort?: (tableSort: TableSort) => void;
}>({
  bleed: true,
  dense: false,
  grid: false,
  currentSort: null,
  setCurrentSort: null,
});

export function Table({
  bleed = true,
  dense = false,
  grid = false,
  currentSort = null,
  setCurrentSort = null,
  className,
  children,
  ...props
}: {
  bleed?: boolean;
  dense?: boolean;
  grid?: boolean;
  currentSort?: TableSort;
  setCurrentSort?: (tableSort: TableSort) => void;
} & React.ComponentPropsWithoutRef<'div'>) {
  return (
    <TableContext.Provider
      value={
        {
          bleed,
          dense,
          grid,
          currentSort,
          setCurrentSort,
        } as React.ContextType<typeof TableContext>
      }
    >
      <div className='flow-root'>
        <div
          {...props}
          className={clsx(className, '-mx-[--gutter] overflow-x-auto whitespace-nowrap')}
        >
          <div
            className={clsx('inline-block min-w-full align-middle', !bleed && 'sm:px-[--gutter]')}
          >
            <table className='min-w-full text-left text-sm/6 text-zinc-700 dark:text-white'>
              {children}
            </table>
          </div>
        </div>
      </div>
    </TableContext.Provider>
  );
}

export function TableHead({ className, ...props }: React.ComponentPropsWithoutRef<'thead'>) {
  return (
    <thead {...props} className={clsx(className, 'bg-zinc-100 text-zinc-950 dark:text-zinc-700')} />
  );
}

export function TableBody(props: React.ComponentPropsWithoutRef<'tbody'>) {
  return <tbody {...props} />;
}

const TableRowContext = createContext<{
  href?: string;
  target?: string;
  title?: string;
}>({
  href: undefined,
  target: undefined,
  title: undefined,
});

export const TableRow = forwardRef<
  HTMLTableRowElement,
  {
    href?: string;
    target?: string;
    title?: string;
  } & React.ComponentPropsWithoutRef<'tr'>
>(({ href, target, title, className, ...props }, ref) => {
  return (
    <TableRowContext.Provider value={{ href, target, title }}>
      <tr
        ref={ref}
        {...props}
        className={clsx(
          className,
          href &&
            'has-[[data-row-link][data-focus]]:outline has-[[data-row-link][data-focus]]:outline-2 has-[[data-row-link][data-focus]]:-outline-offset-2 has-[[data-row-link][data-focus]]:outline-sky-500 dark:focus-within:bg-white/[2.5%]',
          href && 'hover:bg-zinc-950/[2.5%] dark:hover:bg-white/[2.5%]',
        )}
      />
    </TableRowContext.Provider>
  );
});

TableRow.displayName = 'TableRow'; // For better debugging experience

export function TableHeader({
  sortColumn,
  className,
  ...props
}: { sortColumn?: string } & React.ComponentPropsWithoutRef<'th'>) {
  const { bleed, grid, currentSort, setCurrentSort } = useContext(TableContext);

  const handleSortChange = () => {
    if (currentSort?.column !== sortColumn) {
      setCurrentSort({ column: sortColumn, direction: 'asc' });
    } else {
      switch (currentSort.direction) {
        case 'asc':
          setCurrentSort({ column: sortColumn, direction: 'desc' });
          break;
        case 'desc':
          setCurrentSort({ column: sortColumn, direction: 'asc' });
          break;
        default:
          setCurrentSort({ column: sortColumn, direction: 'asc' });
          break;
      }
    }
  };

  const getIcon = (sortColumn: string) => {
    const result = <ChevronUpDownIcon className='size-4' />;
    if (currentSort?.column === sortColumn) {
      return (
        <ChevronDownIcon
          className={cn(
            'transition-transform size-4',
            currentSort.direction === 'desc' ? 'rotate-180' : 'rotate-0',
          )}
        />
      );
    }
    return result;
  };

  return (
    <th
      {...props}
      className={clsx(
        className,
        'border-b-zinc-950/10 p-1 font-semibold first:pl-[var(--gutter,theme(spacing.2))] last:pr-[var(--gutter,theme(spacing.2))] dark:border-b-white/10',
        grid && 'border-l border-l-zinc-950/5 first:border-l-0 dark:border-l-white/5',
        !bleed && 'sm:first:pl-1 sm:last:pr-1',
        'first:rounded-l-xl last:rounded-r-xl', // if first child, give left side rounded-xl
      )}
    >
      {sortColumn && (
        <Button variant='light' onPress={() => handleSortChange()}>
          {props.children} {getIcon(sortColumn)}
        </Button>
      )}
      {!sortColumn && props.children}
    </th>
  );
}

export function TableCell({ className, children, ...props }: React.ComponentPropsWithoutRef<'td'>) {
  const { bleed, dense, grid } = useContext(TableContext);
  const { href, target, title } = useContext(TableRowContext);
  const [cellRef, setCellRef] = useState<HTMLElement | null>(null);

  return (
    <td
      ref={href ? setCellRef : undefined}
      {...props}
      className={clsx(
        className,
        'relative px-4 first:pl-[var(--gutter,theme(spacing.2))] last:pr-[var(--gutter,theme(spacing.2))] border-b border-zinc-950/5 dark:border-white/5',
        grid && 'border-l border-l-zinc-950/5 first:border-l-0 dark:border-l-white/5',
        dense ? 'py-2.5' : 'py-5',
        !bleed && 'sm:first:pl-1 sm:last:pr-1',
      )}
    >
      {href && (
        <Link
          data-row-link
          href={href}
          target={target}
          aria-label={title}
          tabIndex={cellRef?.previousElementSibling === null ? 0 : -1}
          className='absolute inset-0 focus:outline-none'
        />
      )}
      {children}
    </td>
  );
}
